import { currentLocale, translate } from './i18n';

/**
 * Every number, date and enum the console prints.
 *
 * The formatters read the active locale on each call rather than being built
 * once at import: the operator can change language mid-session, and a cached
 * `Intl` instance from before the switch would keep printing "1,250" on a
 * Turkish screen. The instances themselves are memoised per locale, so the
 * switch costs one construction rather than one per row.
 */

const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>();

function memo<T extends Intl.NumberFormat | Intl.DateTimeFormat>(
  kind: string,
  build: (locale: string) => T,
): T {
  const locale = currentLocale();
  const key = `${kind}:${locale}`;
  const existing = cache.get(key);
  if (existing) return existing as T;

  const created = build(locale);
  cache.set(key, created);
  return created;
}

const numberFormat = () =>
  memo('number', (locale) => new Intl.NumberFormat(locale));

const compactFormat = () =>
  memo(
    'compact',
    (locale) =>
      new Intl.NumberFormat(locale, {
        notation: 'compact',
        maximumFractionDigits: 1,
      }),
  );

const dateTimeFormat = () =>
  memo(
    'dateTime',
    (locale) =>
      new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
  );

const dateFormat = () =>
  memo(
    'date',
    (locale) =>
      new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }),
  );

export function formatNumber(value: number | null | undefined): string {
  return numberFormat().format(value ?? 0);
}

/** Compact form for tight spots — axis ticks and stat tiles above 5 digits. */
export function formatCompact(value: number | null | undefined): string {
  const n = value ?? 0;
  return Math.abs(n) < 10_000
    ? numberFormat().format(n)
    : compactFormat().format(n);
}

/**
 * Credits, with a suffix. Not a currency: these are in-app credits, and
 * formatting them as money would imply a withdrawal that does not exist.
 */
export function formatCredits(value: number | null | undefined): string {
  return `${formatNumber(value)} ${translate('common.creditSuffix')}`;
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : dateTimeFormat().format(date);
}

export function formatDay(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : dateFormat().format(date);
}

/** "3 minutes ago" / "in 5 days" — for last-seen and expiry columns. */
export function formatRelative(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const seconds = (date.getTime() - Date.now()) / 1000;
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3_600],
    ['minute', 60],
  ];
  const relative = new Intl.RelativeTimeFormat(currentLocale(), {
    numeric: 'auto',
  });

  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) {
      return relative.format(Math.round(seconds / size), unit);
    }
  }
  return relative.format(Math.round(seconds), 'second');
}

/**
 * A duration as the operator reads it. Challenge times are single-digit
 * seconds and sub-second differences decide who was fastest, so below a minute
 * this keeps one decimal rather than rounding two winners into a tie.
 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1_000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1_000).toFixed(1)}s`;

  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1_000);
  return `${minutes}m ${seconds}s`;
}

/** Bytes as the operator reads a file size — `48.3 MB`. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const digits = unit === 0 || value >= 100 ? 0 : 1;
  return `${new Intl.NumberFormat(currentLocale(), {
    maximumFractionDigits: digits,
  }).format(value)} ${units[unit]}`;
}

/**
 * Kuruş as lira in the operator's locale — `12900` → `₺129.00` / `129,00 ₺`.
 * The one place money is printed; credits keep {@link formatCredits}, because
 * the two must never be mistaken for one another.
 */
export function formatMoneyMinor(minor: number | null | undefined): string {
  return memo(
    'money',
    (locale) =>
      new Intl.NumberFormat(locale, { style: 'currency', currency: 'TRY' }),
  ).format((minor ?? 0) / 100);
}

/**
 * The same money, compacted once it stops fitting — `₺1.250,00`, then `₺1,3B`.
 *
 * For a stat tile, where a day's takings share a quarter of the row with three
 * other figures. Below five digits nothing is compacted, because that is the
 * range where the kuruş still mean something.
 */
export function formatMoneyCompact(minor: number | null | undefined): string {
  const lira = (minor ?? 0) / 100;
  if (Math.abs(lira) < 10_000) return formatMoneyMinor(minor);

  return memo(
    'moneyCompact',
    (locale) =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'TRY',
        notation: 'compact',
        maximumFractionDigits: 1,
      }),
  ).format(lira);
}

/**
 * A long span of time, coarsely — `12d 4h`, `3h 20m`, `45m`. For what is left
 * of a subscription, where seconds would only be noise.
 */
export function formatSpan(seconds: number | null | undefined): string {
  const total = Math.max(0, Math.floor(seconds ?? 0));
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  return `${minutes}m`;
}

/** A percentage of a total, guarding the zero-total case. */
export function percentOf(value: number, total: number): string {
  if (!total) return formatPercent(0);
  return formatPercent(Math.round((value / total) * 100));
}

/** A 0…1 rate as a percentage. */
export function formatRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '—';
  return formatPercent(Math.round(rate * 100));
}

/** Turkish writes the sign before the number: `%40`, not `40%`. */
export function formatPercent(value: number): string {
  return currentLocale().startsWith('tr') ? `%${value}` : `${value}%`;
}

/** Platform names as their vendors spell them; `capitalize` gets iOS wrong. */
export function formatPlatform(platform: string): string {
  if (platform === 'ios') return 'iOS';
  if (platform === 'android') return 'Android';
  return platform.charAt(0).toUpperCase() + platform.slice(1);
}

/**
 * An enum value the API sends raw, as a sentence.
 *
 * Translations live under `enum.*`; anything without one falls back to the old
 * behaviour of un-snaking and capitalising, which keeps a value the backend
 * adds tomorrow readable rather than blank.
 */
export function humanize(value: string | null | undefined): string {
  if (!value) return '—';

  const translated = translate(`enum.${value}`);
  if (translated !== `enum.${value}`) return translated;

  const spaced = value.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * The schedule string the alarms API composes in English.
 *
 * It is a small closed vocabulary — "Once", "Every day", or a comma-separated
 * list of weekday abbreviations — so translating the pieces here is cheaper
 * and safer than teaching the endpoint about locales.
 */
export function formatSchedule(label: string): string {
  const whole = translate(`schedule.${label}`);
  if (whole !== `schedule.${label}`) return whole;

  return label
    .split(', ')
    .map((part) => {
      const day = translate(`schedule.${part}`);
      return day === `schedule.${part}` ? part : day;
    })
    .join(', ');
}

/** The operator's UTC offset in minutes, as the dashboard endpoints expect. */
export function timezoneOffsetMinutes(): number {
  return -new Date().getTimezoneOffset();
}

/** Midnight local time [days] ago, as an ISO instant. */
export function daysAgoIso(days: number): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - days);
  return date.toISOString();
}

/** A short, stable label for an id — enough to recognise, not to read out. */
export function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.length <= 12 ? id : `${id.slice(0, 8)}…`;
}
